import {
  getInMemoryClipboardHTML,
  writePreparedCopy
} from '#react/app/editor/clipboard'
import type { EditorStore } from '#react/app/editor/store'
import { documentAccessStore, hasDocumentCapability } from '#react/app/document/access'
import { hasDocumentTextSelection, isEditing } from '#react/app/shell/keyboard/focus'
import { hydrateHostedInstances } from '#react/hosted-components'
import { resolveSelectedInsertionParent } from '#react/controls/component-props/slot-insert'

const RASTER_IMAGE_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'image/avif'
])

function cursorPosition(store: EditorStore) {
  const { cursorCanvasX: ccx, cursorCanvasY: ccy } = store.state
  return ccx != null && ccy != null ? { x: ccx, y: ccy } : undefined
}

export function extractImageFilesFromClipboard(e: ClipboardEvent): File[] {
  const files = e.clipboardData?.files
  return files ? Array.from(files).filter((file) => RASTER_IMAGE_TYPES.has(file.type)) : []
}

export async function copyAndDeleteSelection(
  store: EditorStore,
  clipboardData: DataTransfer
): Promise<boolean> {
  try {
    const wrote = await writePreparedCopy(store, clipboardData)
    if (!wrote) return false
    store.deleteSelected()
    return true
  } catch (error) {
    console.warn('Browser clipboard cut failed', error)
    return false
  }
}

function pasteIntoInsertionParent(
  store: EditorStore,
  html: string,
  cursorPos: { x: number; y: number } | undefined
) {
  const parentId = resolveSelectedInsertionParent(store)
  const previous = store.state.enteredContainerId
  if (parentId !== store.state.currentPageId) store.state.enteredContainerId = parentId
  return store.pasteFromHTML(html, cursorPos).finally(() => {
    store.state.enteredContainerId = previous
    hydrateHostedInstances(store)
  })
}

export function bindEditorClipboard(store: EditorStore) {
  function onCopy(e: ClipboardEvent) {
    if (store.state.pageLoading.visible || store.state.loading) return
    // Let the browser copy highlighted UI / panel text outside the canvas.
    if (isEditing(e) || hasDocumentTextSelection()) return
    if (!hasDocumentCapability(documentAccessStore.get(), 'copy')) return
    e.preventDefault()
    if (e.clipboardData) void writePreparedCopy(store, e.clipboardData)
  }

  function onCut(e: ClipboardEvent) {
    if (store.state.pageLoading.visible || store.state.loading) return
    if (isEditing(e)) return
    if (
      !hasDocumentCapability(documentAccessStore.get(), 'edit') ||
      !hasDocumentCapability(documentAccessStore.get(), 'copy')
    ) {
      return
    }
    e.preventDefault()
    if (e.clipboardData) void copyAndDeleteSelection(store, e.clipboardData)
  }

  function onPaste(e: ClipboardEvent) {
    if (store.state.pageLoading.visible || store.state.loading) return
    if (isEditing(e)) return
    if (!hasDocumentCapability(documentAccessStore.get(), 'edit')) return
    e.preventDefault()

    const cursorPos = cursorPosition(store)

    const imageFiles = extractImageFilesFromClipboard(e)
    if (imageFiles.length) {
      const cx = cursorPos?.x ?? (-store.state.panX + window.innerWidth / 2) / store.state.zoom
      const cy = cursorPos?.y ?? (-store.state.panY + window.innerHeight / 2) / store.state.zoom
      void store.placeImageFiles(imageFiles, cx, cy)
      return
    }

    const html = e.clipboardData?.getData('text/html') ?? ''
    if (html) {
      void pasteIntoInsertionParent(store, html, cursorPos)
      return
    }

    const memoryHTML = getInMemoryClipboardHTML()
    if (memoryHTML) {
      void pasteIntoInsertionParent(store, memoryHTML, cursorPos)
    }
  }

  window.addEventListener('copy', onCopy)
  window.addEventListener('cut', onCut)
  window.addEventListener('paste', onPaste)
  return () => {
    window.removeEventListener('copy', onCopy)
    window.removeEventListener('cut', onCut)
    window.removeEventListener('paste', onPaste)
  }
}
